import {useEffect, useRef, useState} from "react";

type useCartTimerReturn = {
  secondsLeft: number;
  isExpired: boolean;
};

type useCartTimerProps = {
  secondsToCartExpiry: number;
  onTimerExpire?: () => void;
};

export default function useCartTimer({
  secondsToCartExpiry,
  onTimerExpire = () => null,
}: useCartTimerProps): useCartTimerReturn {
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [expiryTime, setExpiryTime] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    setSecondsLeft(0);

    if (secondsToCartExpiry > 0) {
      setSecondsLeft(secondsToCartExpiry);
      setExpiryTime(Date.now() + secondsToCartExpiry * 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      setSecondsLeft(0);
    };
  }, [secondsToCartExpiry]);

  useEffect(() => {
    if (secondsLeft > 0) {
      timerRef.current = setTimeout(() => {
        setSecondsLeft((expiryTime - Date.now()) / 1000);
      }, 1000);
    } else if (timerRef.current) {
      onTimerExpire();
    }
  }, [secondsLeft, expiryTime]);

  const isExpired =
    secondsLeft <= 0 && secondsToCartExpiry > 0 && Boolean(timerRef.current);

  return {
    secondsLeft,
    isExpired,
  };
}
